import classnames from 'classnames';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ALERT_CENTER_NS } from 'src/alerts/AlertCenter.constants';
import { resolveAlertTierNameKey } from 'src/alerts/AlertCenter.functions';
import UpgradeTierModal from 'src/components/LeadAgentAcceptanceBanner/modals/UpgradeTierModal/UpgradeTierModal.component';
import {
  computeCapacityFillPercent,
  formatUsageResetDate,
} from 'src/components/LeadAgentUsageBanner/LeadAgentUsageBanner.functions';
import { useLeadAgentAcceptanceContext } from 'src/providers/LeadAgentAcceptanceProvider/LeadAgentAcceptance.context';
import { useWorkspaceContext } from 'src/providers/WorkspaceProvider/Workspace.context';
import type { tAlertDetailRendererProps } from './AlertDetail.registry';
import AlertDetailBody from './AlertDetailBody.component';

const NS = 'common/leadAgentUsageBanner';

/**
 * Rich body for the Lead Agent capacity alerts (U4/RET-1878): the capacity
 * gauge, reset date and tier badge, adopted from `LeadAgentUsageBanner`'s
 * large variant (same i18n keys, same fill math). The legacy banner keeps its
 * own copy until RET-1879 retires it.
 *
 * Reads the LIVE acceptance context rather than the alert's frozen values, so
 * the gauge tracks consumption while the modal is open. The phase comes from
 * the alert id being viewed — never re-derived from the flags, which could
 * disagree mid-transition with the alert the user opened.
 *
 * CARRIES THE CHECKOUT since 2026-08-31. "Ampliar Lead Agent" used to navigate
 * to the Smart Leadbot config page, which has no upgrade affordance — the
 * legacy banner had ALREADY fixed that by mounting `UpgradeTierModal` in place
 * ("it used to navigate to Ajustes > Suscripción as a placeholder, a dead-end
 * with no upgrade affordance"), and the port to the Alert Center undid it. Same
 * wiring as that container, nested-modal contract included (REL-2).
 */
export default function LeadAgentUsageDetail(props: tAlertDetailRendererProps) {
  const { t } = useTranslation(NS);
  const { t: tAlerts } = useTranslation(ALERT_CENTER_NS);
  const { usageCapacityPct, usageResetDate, usageTier } =
    useLeadAgentAcceptanceContext();
  // `workspaceId` comes from the WorkspaceProvider, NOT the acceptance context:
  // the capacity phase only renders when there is no acceptance row, so that
  // context's workspaceId is unavailable (the legacy container's note).
  const { currentWorkspace } = useWorkspaceContext();

  const [isUpgradeOpen, setIsUpgradeOpen] = useState(false);
  /*
   * Optimistic, session-local hide once the upgrade lands, adopted from the
   * legacy container: the higher cap provisions through the Stripe webhook, so
   * the warning is stale before the provider refetches. Only ever HIDES, never
   * shows — the safe drift direction, same trade as the expired detail's
   * `markResolved`.
   */
  const [isUpgraded, setIsUpgraded] = useState(false);

  // While the checkout is up the host detail must not close (REL-2 by
  // composition). Cleanup releases the lock if this renderer unmounts for its
  // own reasons.
  const { onNestedModalChange } = props;
  useEffect(() => {
    onNestedModalChange(isUpgradeOpen);
    return () => onNestedModalChange(false);
  }, [isUpgradeOpen, onNestedModalChange]);

  const tierKey = usageTier ? resolveAlertTierNameKey(usageTier) : null;
  const tierName = tierKey ? tAlerts(tierKey) : null;

  const isCapped = props.alert.id === 'la-usage-capped';
  const phase = isCapped ? 'capped' : 'nearLimit';
  // A capped alert renders a full bar even if the usage payload is briefly
  // null (the phase is the authority, mirroring the legacy banner's clamp).
  const capacity = usageCapacityPct ?? (isCapped ? 100 : 0);
  const fillPercent = isCapped ? 100 : computeCapacityFillPercent(capacity);
  const resetDateShort = formatUsageResetDate(usageResetDate);

  return (
    <div className="AlertDetail" data-testid="alert-detail-la-usage">
      <AlertDetailBody alert={props.alert} />
      {/* Reading, tier and reset date as one unboxed group — the prototype's
          `.usage`, same treatment the trial-cap detail uses. */}
      <div className="AlertDetail__Meter">
        <div className="AlertDetail__MeterMeta">
          <span
            className={classnames('AlertDetail__MeterLabel', {
              'AlertDetail__MeterLabel--capped': isCapped,
            })}
          >
            {t('capacityLabel')}
          </span>
          {/* F6 — the DISPLAY name, not the billing code. This badge read "Plan
            GROWTH" while the same tier rendered as "Growth" everywhere else.
            An unrecognised code hides the badge rather than printing the
            identifier, the same fail-closed rule the snapshot follows. */}
          {tierName ? (
            <span
              className="AlertDetail__TierBadge"
              data-testid="alert-detail-usage-tier"
            >
              {t('tierBadge', { tier: tierName })}
            </span>
          ) : null}
        </div>
        <div
          className="AlertDetail__MeterBar"
          role="progressbar"
          aria-valuenow={capacity}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={t('capacityLabel')}
        >
          <div
            className={classnames('AlertDetail__MeterFill', {
              // Red only once it actually stopped. The alert id is the authority
              // here for the same reason the fill percent is: the flags can
              // disagree mid-transition with the alert the user opened.
              'AlertDetail__MeterFill--capped': isCapped,
            })}
            data-testid="alert-detail-usage-fill"
            // Dynamic fill width — the codebase's accepted exception to the
            // no-inline-style rule (same as the legacy banner's timebar).
            style={{ width: `${fillPercent}%` }}
          />
        </div>
        {/* The prototype's note states the CONSEQUENCE and bolds the date
            ("dejará de responder hasta tu renovación, el <b>27 jun 2026</b>").
            The usage namespace's `resetNote` states a neutral fact instead
            ("tu capacidad se reinicia el…"), so the Alert Center carries its
            own — the legacy banner's pinned copy stays untouched. */}
        {resetDateShort ? (
          <p
            className="AlertDetail__Note"
            data-testid="alert-detail-usage-reset"
          >
            <Trans
              t={tAlerts}
              i18nKey={`detail.leadAgentUsage.${isCapped ? 'capped' : 'near'}.note`}
              values={{ resetDate: resetDateShort }}
              components={{ b: <b /> }}
            />
          </p>
        ) : null}
      </div>

      {/* The action, where the money is. Hidden once the upgrade lands so the
          dialog stops offering what the Owner just bought. */}
      {isUpgraded ? null : (
        <div className="AlertDetail__Actions">
          <button
            type="button"
            className="AlertDetail__OptionCta AlertDetail__OptionCta--primary"
            onClick={() => setIsUpgradeOpen(true)}
            data-testid="alert-detail-usage-upgrade"
          >
            {tAlerts(
              `leadAgentUsage.${phase === 'capped' ? 'capped' : 'near'}.cta`,
            )}
          </button>
        </div>
      )}

      {isUpgradeOpen ? (
        <UpgradeTierModal
          workspaceId={currentWorkspace?.id ?? ''}
          currentTier={usageTier}
          currentCapacityPct={usageCapacityPct}
          capacityResetDate={usageResetDate}
          onClose={() => setIsUpgradeOpen(false)}
          onUpgraded={() => {
            setIsUpgradeOpen(false);
            setIsUpgraded(true);
          }}
        />
      ) : null}
    </div>
  );
}
